import { useCallback, useEffect, useState, type FC } from "react";
import {
  ComponentBaseContainer,
  ComponentContainer,
  JsonContainer,
  StyledCopyIcon,
  StyledRefreshIcon,
} from "../jsonformatter/JsonFormatter.style";
import SideNav from "../nav/SideNav";
import {
  CronContainer,
  CronValue,
  CronValueContainer,
} from "./CronGenerator.style";
import Divider from "@mui/material/Divider";
import { Chip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import AdsComponent from "../AdsComponent";
import Minutes from "./partials/Minute";
import Hours from "./partials/Hours";
import DayOfMonth from "./partials/DayOfMonth";
import Month from "./partials/Month";
import DayOfWeek from "./partials/DayOfWeek";
import { CronExpressionParser } from 'cron-parser';
import cronstrue from 'cronstrue';
import { Helmet } from "react-helmet-async";


const CronGenerator: FC = () => {
  const [cronValues, setCronValues] = useState({
    minute: "*",
    hour: "*",
    day: "*",
    month: "*",
    weekDay: "*",
  });

  const [dateError, setDateError] = useState<string>("");
  const [clear, setClear] = useState<boolean>(false);
  const [nextRuns, setNextRuns] = useState<string[]>([]);


  const getNextRuns = useCallback(() => {
    try {
      const cronExpression = `${cronValues.minute} ${cronValues.hour} ${cronValues.day} ${cronValues.month} ${cronValues.weekDay}`;
      const interval = CronExpressionParser.parse(cronExpression);
      const nextRuns: string[] = [];

      for (let i = 0; i < 5; i++) {
        nextRuns.push(interval.next().toString());
      }

      setNextRuns(nextRuns);
    } catch (err) {
      console.error("Invalid cron expression:", err);
      setNextRuns([]);
    }
  }, [cronValues]);

  useEffect(() => {
    setTimeout(() => validateDayMonth(cronValues.day, cronValues.month), 0);
    getNextRuns();
  }, [cronValues, getNextRuns])
  const [showToast, setShowToast] = useState(false);

  const validateDayMonth = (day: string, month: string) => {
    if (day === "*" || month === "*" || day.includes("-") || month.includes("-")) {
      setDateError("");
      return;
    }

    const parsedDay = Number(day.split('/')[1] || day);
    const parsedMonth = Number(month.split('/')[1] || month);

    const isValid = DateTime.fromObject({ day: parsedDay, month: parsedMonth }).isValid;
    setDateError(isValid ? "" : `Invalid date: Day ${parsedDay} doesn't exist in month ${parsedMonth}, ${new Date().getFullYear()}`);
  };

  const handleCopy = () => {
    const content = `${cronValues.minute} ${cronValues.hour} ${cronValues.day} ${cronValues.month} ${cronValues.weekDay}`;
    content && navigator.clipboard.writeText(content)
      .then(() => {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 1000);
      })
  }

  const handleNextRunCopy = () => {

    navigator.clipboard.writeText(nextRuns.join('\n'))
      .then(() => {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 1000);
      })
  }

  const handleCronClear = () => {
    setCronValues({
      minute: "*",
      hour: "*",
      day: "*",
      month: "*",
      weekDay: "*",
    })
    setClear(true)
    setTimeout(() => setClear(false), 0);
  }



  return (
    <ComponentBaseContainer>
      <Helmet>
        <title>Cron Expression Generator | Easy Cron Builder</title>
        <meta name="description" content="Generate cron expressions easily with this interactive cron builder. Supports minute, hour, day, month, and more." />
        <meta name="keywords" content="cron expression generator, cron builder, cron job tool, cron schedule maker, crontab" />
      </Helmet>

      <SideNav />
      <ComponentContainer>
        <CronContainer>
          <h1>CRON GENERATOR</h1>

          <CronValueContainer>
            <CronValue>{cronValues.minute}</CronValue>
            <CronValue>{cronValues.hour}</CronValue>
            <CronValue>{cronValues.day}</CronValue>
            <CronValue>{cronValues.month}</CronValue>
            <CronValue>{cronValues.weekDay}</CronValue>
            <StyledCopyIcon style={{ marginBottom: "10px" }} onClick={handleCopy} />
          </CronValueContainer>

          {dateError && (
            <Typography
              variant="body2"
              color="error"
              style={{ marginTop: "10px" }}
            >
              {dateError}
            </Typography>
          )}

          <Divider style={{ color: "black", width: "90%", margin: "20px 0" }} />

          <Minutes setCronValues={setCronValues} clear={clear} />
          <Hours clear={clear} setCronValues={setCronValues} />
          <DayOfMonth clear={clear} setCronValues={setCronValues} />
          <Month
            clear={clear}
            setCronValues={setCronValues}
            selectedDay={cronValues.day}
          />
          <DayOfWeek clear={clear} setCronValues={setCronValues} />
          <Chip label="Clear" variant="outlined" onClick={handleCronClear} style={{ marginTop: "10%", width: "50%" }} />
        </CronContainer>
        <Divider orientation="vertical" />
        <JsonContainer style={{ color: "#333", justifyContent: "center", alignItems: "center" }}>

          <div style={{ fontSize: "35px", display: "flex" }}>Description</div>
          <Divider style={{ width: "50%", margin: "10px" }} />
          <div style={{ width: "70%", marginBottom: "10%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {cronstrue.toString(`${cronValues.minute} ${cronValues.hour} ${cronValues.day} ${cronValues.month} ${cronValues.weekDay}`).concat(".")}
          </div>

          <div style={{ fontSize: "35px", display: "flex" }}>Next five runs
            <div style={{ margin: "5px" }}>
              <StyledRefreshIcon onClick={getNextRuns} />
              <StyledCopyIcon onClick={handleNextRunCopy} />
            </div>
          </div>

          <Divider style={{ width: "50%", margin: "10px" }} />
          <div>
            {
              nextRuns.map((nextRun) => {
                return <div style={{ margin: "5px" }}>{nextRun}</div>
              })
            }
          </div>
        </JsonContainer>
        {showToast && (
          <div style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#323232",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            opacity: 0.9,
            fontSize: "14px",
          }}>
            Copied to clipboard!
          </div>
        )}
      </ComponentContainer>
      <AdsComponent />
    </ComponentBaseContainer>
  );
};

export default CronGenerator;
